// Copyright The OpenTelemetry Authors
// SPDX-License-Identifier: Apache-2.0

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ApiGateway from '../gateways/Api.gateway';
import { CartItem, OrderResult, PlaceOrderRequest } from '../protos/demo';
import { IProductCart } from '../types/Cart';
import { useCurrency } from './Currency.provider';

interface IContext {
  cart: IProductCart;
  addItem(item: CartItem): Promise<void>;
  emptyCart(): Promise<void>;
  removeItem(productId: string): Promise<void>;
  setItemQuantity(productId: string, quantity: number): Promise<void>;
  isCartBusy: boolean;
  cartError: string;
  placeOrder(order: PlaceOrderRequest): Promise<OrderResult>;
  isDrawerOpen: boolean;
  openDrawer(): void;
  closeDrawer(): void;
}

export const Context = createContext<IContext>({
  cart: { userId: '', items: [] },
  addItem: () => Promise.resolve(),
  emptyCart: () => Promise.resolve(),
  removeItem: () => Promise.resolve(),
  setItemQuantity: () => Promise.resolve(),
  isCartBusy: false,
  cartError: '',
  placeOrder: () => Promise.resolve({} as OrderResult),
  isDrawerOpen: false,
  openDrawer: () => {},
  closeDrawer: () => {},
});

interface IProps {
  children: React.ReactNode;
}

export const useCart = () => useContext(Context);

const CartProvider = ({ children }: IProps) => {
  const { selectedCurrency } = useCurrency();
  const queryClient = useQueryClient();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCartBusy, setIsCartBusy] = useState(false);
  const [cartError, setCartError] = useState('');
  const operationQueue = useRef<Promise<void>>(Promise.resolve());
  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  const { data: cart = { userId: '', items: [] }, isFetching } = useQuery({
    queryKey: ['cart', selectedCurrency],
    queryFn: () => ApiGateway.getCart(selectedCurrency)
  });
  const pendingOps = useRef(0);
  const itemsRef = useRef<{ productId: string; quantity: number }[]>([]);
  const orderInFlight = useRef<Promise<OrderResult> | null>(null);
  const snapshotReady = useRef(false);
  const fetchStartedAt = useRef(0);
  const lastOpEndedAt = useRef(0);

  useEffect(() => {
    if (isFetching) {
      fetchStartedAt.current = Date.now();
    }
  }, [isFetching]);

  useEffect(() => {
    if (pendingOps.current === 0 && (!snapshotReady.current || fetchStartedAt.current > lastOpEndedAt.current)) {
      itemsRef.current = cart.items.map(({ productId, quantity }) => ({ productId, quantity }));
      snapshotReady.current = true;
    }
  }, [cart]);

  const addCartMutation = useMutation({
    mutationFn: ApiGateway.addCartItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
  });
  const emptyCartMutation = useMutation({
    mutationFn: ApiGateway.emptyCart,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
  });
  const placeOrderMutation = useMutation({
    mutationFn: ApiGateway.placeOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
  });

  const runExclusive = useCallback(<T,>(operation: () => Promise<T>, errorMessage = "Couldn't update your cart. Please try again."): Promise<T> => {
    pendingOps.current += 1;
    setIsCartBusy(true);
    const run = operationQueue.current
      .catch(() => {})
      .then(async () => {
        setCartError('');
        await queryClient.cancelQueries({ queryKey: ['cart'] });
        try {
          return await operation();
        } catch (error) {
          setCartError(errorMessage);
          throw error;
        } finally {
          pendingOps.current -= 1;
          lastOpEndedAt.current = Date.now();
          if (pendingOps.current === 0) {
            setIsCartBusy(false);
          }
        }
      });
    operationQueue.current = run.then(
      () => undefined,
      () => undefined
    );
    return run;
  }, [queryClient]);

  const ensureSnapshot = useCallback(async () => {
    if (!snapshotReady.current) {
      const server = await ApiGateway.getCart(selectedCurrency);
      itemsRef.current = server.items.map(({ productId, quantity }) => ({ productId, quantity }));
      snapshotReady.current = true;
    }
  }, [selectedCurrency]);

  const rebuildCart = useCallback(
    async (items: { productId: string; quantity: number }[]) => {
      let restoreFailed = false;
      await ApiGateway.emptyCart();
      for (const { productId, quantity } of items) {
        let restored = false;
        for (let attempt = 0; attempt < 3 && !restored; attempt += 1) {
          try {
            await ApiGateway.restoreCartItem({ productId, quantity, currencyCode: selectedCurrency });
            restored = true;
          } catch {
            restored = false;
          }
        }
        if (!restored) {
          restoreFailed = true;
        }
      }
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      if (restoreFailed) {
        throw new Error('Some items could not be restored while updating the cart');
      }
      itemsRef.current = items;
    },
    [selectedCurrency, queryClient]
  );

  const addItem = useCallback(
    (item: CartItem) =>
      runExclusive(async () => {
        await addCartMutation.mutateAsync({ ...item, currencyCode: selectedCurrency });
        const existing = itemsRef.current.find(entry => entry.productId === item.productId);
        itemsRef.current = existing
          ? itemsRef.current.map(entry =>
              entry.productId === item.productId ? { ...entry, quantity: entry.quantity + item.quantity } : entry
            )
          : [...itemsRef.current, { productId: item.productId, quantity: item.quantity }];
      }),
    [runExclusive, addCartMutation, selectedCurrency]
  );
  const emptyCart = useCallback(
    () => runExclusive(async () => {
      await emptyCartMutation.mutateAsync();
      itemsRef.current = [];
    }),
    [runExclusive, emptyCartMutation]
  );
  const removeItem = useCallback(
    (productId: string) =>
      runExclusive(async () => {
        await ensureSnapshot();
        const remaining = itemsRef.current.filter(item => item.productId !== productId);
        await rebuildCart(remaining);
      }),
    [runExclusive, ensureSnapshot, rebuildCart]
  );
  const setItemQuantity = useCallback(
    (productId: string, quantity: number) =>
      runExclusive(async () => {
        await ensureSnapshot();
        const current = itemsRef.current.find(item => item.productId === productId);
        if (!current || quantity === current.quantity) {
          return;
        }
        if (quantity > current.quantity) {
          await addCartMutation.mutateAsync({
            productId,
            quantity: quantity - current.quantity,
            currencyCode: selectedCurrency,
          });
          itemsRef.current = itemsRef.current.map(item =>
            item.productId === productId ? { ...item, quantity } : item
          );
          return;
        }
        const remaining = itemsRef.current
          .map(item => (item.productId === productId ? { ...item, quantity } : item))
          .filter(item => item.quantity > 0);
        await rebuildCart(remaining);
      }),
    [runExclusive, ensureSnapshot, rebuildCart, addCartMutation, selectedCurrency]
  );
  const placeOrder = useCallback(
    (order: PlaceOrderRequest) => {
      if (orderInFlight.current) {
        return orderInFlight.current;
      }
      const run = runExclusive(async () => {
        const result = await placeOrderMutation.mutateAsync({ ...order, currencyCode: selectedCurrency });
        itemsRef.current = [];
        return result;
      }, "Couldn't place your order. Please try again.");
      orderInFlight.current = run;
      run.catch(() => undefined).then(() => {
        orderInFlight.current = null;
      });
      return run;
    },
    [runExclusive, placeOrderMutation, selectedCurrency]
  );

  const value = useMemo(
    () => ({
      cart,
      addItem,
      emptyCart,
      removeItem,
      setItemQuantity,
      isCartBusy,
      cartError,
      placeOrder,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
    }),
    [cart, addItem, emptyCart, removeItem, setItemQuantity, isCartBusy, cartError, placeOrder, isDrawerOpen, openDrawer, closeDrawer]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export default CartProvider;
